// The Price Book — one place for everything Titan charges (Pat, Sep 2026).
//
// Common Items: the things quoted as a single line — the DRILLING rates (each drill's
// day, hour and mobilization) and the items quoted over and over that aren't a machine,
// a person or a part: loops per foot or each, a pole hold by the day, mobilization, a
// crew-and-equipment day. They show up in the T&M quote picker as "Common Items" and
// land on the quote with their rate, which can still be changed for that customer. A
// job keeps the rates it was quoted at, so editing the book never rewrites an old job.
// (It opened as "Standard Items" with drilling on its own tab; Pat renamed it and folded
// drilling in — "Standard T&M" is the name for the labor / material / equipment log.)
//
// The other three tabs are doors for now: equipment, labor and material rates keep
// living where they are until they move in here.
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { apiFetch } from '../../services/apiClient';

const UNITS = [['ft', 'per foot'], ['ea', 'each'], ['hr', 'per hour'], ['day', 'per day'], ['trip', 'per trip']];
const SHOWS_ON = [['', 'Any job'], ['loops', 'Loops'], ['small_jobs', 'Small Jobs'], ['drilling', 'Drilling']];
const TABS = [['common', 'Common Items'], ['equipment', 'Equipment'], ['labor', 'Labor'], ['material', 'Material']];
const DOORS = {
  equipment: { where: 'Equipment → Rates', path: '/admin/equipment', open: 'Open Equipment' },
  labor:     { where: 'the Rate Sheet (labor, truck and equipment rates, with the customer PDF)', path: '/admin/rate-sheet', open: 'Open the Rate Sheet' },
  material:  { where: 'the Material Database', path: '/admin/materials', open: 'Open the Material Database' },
};
const DRILL_FIELDS = [['daily_rate', 'Day rate', 'day'], ['hourly_rate', 'Hour rate', 'hr'], ['mobilization', 'Mobilization', 'trip']];

const cell = { border: '1px solid #d1d5db', borderRadius: 7, padding: '8px 10px', fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box', width: '100%', background: '#fff' };
const th = { fontSize: 11, fontWeight: 800, color: '#fff', textTransform: 'uppercase', letterSpacing: 0.5, textAlign: 'left', padding: '10px 12px' };
const td = { padding: '8px 12px', borderBottom: '1px solid #f1f2f4', verticalAlign: 'middle' };
const sectionHead = { fontSize: 12, fontWeight: 800, color: '#374151', textTransform: 'uppercase', letterSpacing: 0.5, margin: '0 0 8px' };

export default function AdminPriceBook() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('common');
  const [items, setItems] = useState(null);
  const [drills, setDrills] = useState(null);
  const [err, setErr] = useState('');
  const [draft, setDraft] = useState({ name: '', unit: 'ea', rate: '' });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    apiFetch('/standard-items').then(r => setItems(Array.isArray(r) ? r : [])).catch(e => { setItems([]); setErr(e?.error || 'Could not load the common items.'); });
    apiFetch('/drill-rates').then(r => setDrills(Array.isArray(r) ? r : [])).catch(() => setDrills([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  // Saves as it is changed. A save that fails says so and puts the old value back —
  // a price that looks saved and isn't is the worst thing this screen could do.
  async function save(it, patch) {
    const same = Object.keys(patch).every(k => String(patch[k] ?? '') === String(it[k] ?? ''));
    if (same) return;
    setErr('');
    setItems(list => list.map(x => x.id === it.id ? { ...x, ...patch } : x));
    try {
      const upd = await apiFetch(`/standard-items/${it.id}`, { method: 'PATCH', body: JSON.stringify(patch) });
      setItems(list => list.map(x => x.id === it.id ? upd : x));
    } catch (e) {
      setItems(list => list.map(x => x.id === it.id ? it : x));
      setErr(e?.error || `Could not save “${it.name}”.`);
    }
  }
  // A drill's three rates save together (that is how the server takes them).
  async function saveDrill(row, key, raw) {
    const val = raw === '' ? null : Number(raw);
    if ((row[key] ?? null) === val) return;
    const next = { ...row, [key]: val };
    setErr('');
    setDrills(list => list.map(x => x.drill === row.drill ? next : x));
    try {
      const upd = await apiFetch(`/drill-rates/${encodeURIComponent(row.drill)}`, { method: 'PATCH', body: JSON.stringify({ daily_rate: next.daily_rate, hourly_rate: next.hourly_rate, mobilization: next.mobilization }) });
      setDrills(list => list.map(x => x.drill === row.drill ? upd : x));
    } catch (e) {
      setDrills(list => list.map(x => x.drill === row.drill ? row : x));
      setErr(e?.error || `Could not save ${row.drill}’s rates.`);
    }
  }
  async function add() {
    if (!draft.name.trim() || busy) return;
    setBusy(true); setErr('');
    try {
      const it = await apiFetch('/standard-items', { method: 'POST', body: JSON.stringify({ name: draft.name.trim(), unit: draft.unit, rate: draft.rate }) });
      setItems(list => [...(list || []), it]);
      setDraft({ name: '', unit: 'ea', rate: '' });
    } catch (e) { setErr(e?.error || 'Could not add that item.'); }
    setBusy(false);
  }
  async function remove(it) {
    if (!window.confirm(`Take “${it.name}” out of the Price Book? Quotes and jobs that already use it keep their line and their rate.`)) return;
    setErr('');
    try { await apiFetch(`/standard-items/${it.id}`, { method: 'DELETE' }); setItems(list => list.filter(x => x.id !== it.id)); }
    catch (e) { setErr(e?.error || 'Could not remove that item.'); }
  }

  const unsetItems = (items || []).filter(i => i.rate == null).length;
  const unsetDrill = (drills || []).reduce((n, d) => n + DRILL_FIELDS.filter(([k]) => d[k] == null).length, 0);
  const count = items && drills ? items.length + drills.length : null;

  return (
    <AdminLayout>
      <button onClick={() => navigate('/admin/settings')}
        style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: 13, cursor: 'pointer', padding: 0, marginBottom: 12 }}>‹ Settings</button>
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Price Book</h1>
          <p className="admin-page-sub">One place for everything Titan charges. A job keeps the rates it was quoted at, so changing the book never rewrites an old job.</p>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 4, borderBottom: '2px solid #e5e7eb', marginBottom: 16, flexWrap: 'wrap' }}>
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)}
            style={{ padding: '10px 16px', border: 'none', borderBottom: `3px solid ${tab === k ? '#e87722' : 'transparent'}`, marginBottom: -2, background: 'none',
              fontSize: 14, fontWeight: tab === k ? 800 : 600, color: tab === k ? '#1e3a5f' : '#6b7280', cursor: 'pointer' }}>
            {l}{k === 'common' && count != null ? <span style={{ color: '#9ca3af', fontWeight: 600 }}> {count}</span> : null}
          </button>
        ))}
      </div>

      {err && <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8, padding: '10px 14px', color: '#b91c1c', fontSize: 13.5, fontWeight: 600, marginBottom: 12 }}>{err}</div>}

      {tab !== 'common' && (
        <div className="admin-card" style={{ padding: 22, maxWidth: 640 }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: '#111827', marginBottom: 6 }}>{TABS.find(t => t[0] === tab)[1]} rates haven’t moved in yet</div>
          <div style={{ fontSize: 13.5, color: '#4b5563', lineHeight: 1.55, marginBottom: 14 }}>
            They still live in {DOORS[tab].where}, and everything that reads them works as it always has. They move into this tab next.
          </div>
          <button onClick={() => navigate(DOORS[tab].path)} className="admin-primary-btn">{DOORS[tab].open} →</button>
        </div>
      )}

      {tab === 'common' && (items === null || drills === null ? (
        <div style={{ padding: 30, color: '#9ca3af' }}>Loading…</div>
      ) : (
        <>
          {(unsetItems > 0 || unsetDrill > 0) && (
            <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 14px', color: '#92400e', fontSize: 13.5, marginBottom: 14 }}>
              <b>{unsetItems + unsetDrill} rate{unsetItems + unsetDrill === 1 ? ' is' : 's are'} not set yet</b>
              {unsetDrill > 0 ? ` (${unsetDrill} on the drills)` : ''}. They can still go on a quote — the rate is typed on the line — but setting it here means nobody has to remember it.
            </div>
          )}

          {/* Drilling — each drill's day, hour and mobilization. The same three numbers the
              🛠️ Drill Rates button on the Bids page used to edit in a pop-up. */}
          <h2 style={sectionHead}>Drilling</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 12, marginBottom: 22 }}>
            {drills.map(d => (
              <div key={d.drill} className="admin-card" style={{ padding: 14 }}>
                <div style={{ fontSize: 15, fontWeight: 800, color: '#92400e', marginBottom: 10 }}>{d.drill}</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8 }}>
                  {DRILL_FIELDS.map(([k, label, unit]) => (
                    <label key={k} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <span style={{ fontSize: 10.5, fontWeight: 800, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.3 }}>{label}</span>
                      <input type="number" step="any" min="0" inputMode="decimal" defaultValue={d[k] ?? ''} key={`${d.drill}-${k}-${d.updated_at}`} placeholder="set rate"
                        style={{ ...cell, textAlign: 'right', borderColor: d[k] == null ? '#f59e0b' : '#d1d5db' }}
                        onBlur={e => saveDrill(d, k, e.target.value)} />
                      <span style={{ fontSize: 10.5, color: '#9ca3af', textAlign: 'right' }}>per {unit}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
            {drills.length === 0 && <div style={{ fontSize: 13, color: '#9ca3af' }}>No drills set up.</div>}
          </div>

          <h2 style={sectionHead}>Items</h2>
          <div className="admin-card" style={{ overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 820 }}>
                <thead>
                  <tr style={{ background: '#1e3a5f' }}>
                    <th style={th}>Item — as it reads on the quote and the invoice</th>
                    <th style={{ ...th, width: 130 }}>Billed by</th>
                    <th style={{ ...th, width: 130 }}>Rate</th>
                    <th style={{ ...th, width: 110 }}>Minimum</th>
                    <th style={{ ...th, width: 140 }}>Shows on</th>
                    <th style={{ ...th, width: 80, textAlign: 'center' }}>Bundle</th>
                    <th style={{ ...th, width: 44 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(it => (
                    <tr key={it.id}>
                      <td style={td}><input defaultValue={it.name} key={`n-${it.id}-${it.updated_at}`} aria-label="Item name" style={{ ...cell, fontWeight: 700 }}
                        onBlur={e => { const v = e.target.value.trim(); if (v) save(it, { name: v }); else e.target.value = it.name; }} /></td>
                      <td style={td}><select value={it.unit} aria-label="Billed by" onChange={e => save(it, { unit: e.target.value })} style={cell}>
                        {UNITS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></td>
                      <td style={td}><input type="number" step="any" min="0" defaultValue={it.rate ?? ''} key={`r-${it.id}-${it.updated_at}`} placeholder="set rate" aria-label="Rate"
                        style={{ ...cell, textAlign: 'right', borderColor: it.rate == null ? '#f59e0b' : '#d1d5db' }}
                        onBlur={e => save(it, { rate: e.target.value === '' ? null : Number(e.target.value) })} /></td>
                      <td style={td}><input type="number" step="any" min="0" defaultValue={it.min_qty ?? ''} key={`m-${it.id}-${it.updated_at}`} placeholder="none" aria-label="Minimum billed"
                        style={{ ...cell, textAlign: 'right' }}
                        onBlur={e => save(it, { min_qty: e.target.value === '' ? null : Number(e.target.value) })} /></td>
                      <td style={td}><select value={it.workflow || ''} aria-label="Shows on" onChange={e => save(it, { workflow: e.target.value || null })} style={cell}>
                        {SHOWS_ON.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></td>
                      <td style={{ ...td, textAlign: 'center' }}><input type="checkbox" checked={!!it.is_bundle} aria-label="Bundle — crew and equipment under one name"
                        onChange={e => save(it, { is_bundle: e.target.checked ? 1 : 0 })} style={{ width: 18, height: 18 }} /></td>
                      <td style={{ ...td, textAlign: 'center' }}><button onClick={() => remove(it)} title="Take out of the Price Book" aria-label={`Remove ${it.name}`}
                        style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: 18, cursor: 'pointer', lineHeight: 1 }}>×</button></td>
                    </tr>
                  ))}
                  {items.length === 0 && <tr><td style={{ ...td, color: '#9ca3af' }} colSpan={7}>Nothing here yet — add the first thing you quote over and over.</td></tr>}
                  <tr style={{ background: '#faf9f7' }}>
                    <td style={td}><input value={draft.name} placeholder="Add an item — e.g. Traffic control" aria-label="New item name" style={cell}
                      onChange={e => setDraft(d => ({ ...d, name: e.target.value }))} onKeyDown={e => { if (e.key === 'Enter') add(); }} /></td>
                    <td style={td}><select value={draft.unit} aria-label="New item billed by" onChange={e => setDraft(d => ({ ...d, unit: e.target.value }))} style={cell}>
                      {UNITS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></td>
                    <td style={td}><input type="number" step="any" min="0" value={draft.rate} placeholder="rate" aria-label="New item rate" style={{ ...cell, textAlign: 'right' }}
                      onChange={e => setDraft(d => ({ ...d, rate: e.target.value }))} onKeyDown={e => { if (e.key === 'Enter') add(); }} /></td>
                    <td style={td} colSpan={4}><button onClick={add} disabled={!draft.name.trim() || busy} className="admin-primary-btn"
                      style={{ opacity: !draft.name.trim() || busy ? 0.5 : 1 }}>{busy ? 'Adding…' : '+ Add to the Price Book'}</button></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div style={{ fontSize: 12.5, color: '#6b7280', marginTop: 10, lineHeight: 1.55, maxWidth: 820 }}>
            Drilling and these items show up under <b>Common Items</b> when you add a line to a T&amp;M quote or a job’s T&amp;M Rates. The rate lands on the line and can be changed for that customer.
            The field sees the items when logging a day, never the rate. <b>Minimum</b> is recorded here for now; invoices don’t apply it on their own yet.
          </div>
        </>
      ))}
    </AdminLayout>
  );
}
